import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { PreviewPaneHeader } from '@/components/features/preview-pane-header'
import { MarkdownProse } from '@/components/features/shared/markdown-prose'
import {
  MessageSquare,
  Inbox,
  Zap,
  Lightbulb,
  FileText,
  Bookmark,
  FolderOpen,
} from 'lucide-react'
import type { HomeSelection } from '@/components/dashboard/home-selection'
import {
  metaFromHomeSelection,
  titleFromHomeSelection,
} from '@/components/dashboard/home-selection'
import { testIds } from '@/lib/test-ids'
import { cn } from '@/lib/utils'

function iconFor(selection: HomeSelection) {
  switch (selection.kind) {
    case 'chat':
      return <MessageSquare className="h-4 w-4 text-muted-foreground shrink-0" />
    case 'inbox':
      return <Inbox className="h-4 w-4 text-muted-foreground shrink-0" />
    case 'flow':
      return <Zap className="h-4 w-4 text-muted-foreground shrink-0" />
    case 'suggestion':
      return <Lightbulb className="h-4 w-4 text-muted-foreground shrink-0" />
    case 'basecamp-todo':
      return <Bookmark className="h-4 w-4 text-muted-foreground shrink-0" />
    case 'basecamp-project':
      return <FolderOpen className="h-4 w-4 text-muted-foreground shrink-0" />
    default:
      return <FileText className="h-4 w-4 text-muted-foreground shrink-0" />
  }
}

function kindLabel(selection: HomeSelection): string {
  switch (selection.kind) {
    case 'chat':
      return 'Chat'
    case 'inbox':
      return 'Inbox'
    case 'flow':
      return 'Flow'
    case 'suggestion':
      return 'Suggestion'
    case 'basecamp-todo':
      return 'Basecamp Todo'
    case 'basecamp-project':
      return 'Basecamp Project'
    default:
      return 'Preview'
  }
}

function formatRelativeTime(iso: string | null | undefined): string {
  if (!iso) return 'Never'
  const t = new Date(iso).getTime()
  if (Number.isNaN(t)) return 'Unknown'
  const m = Math.floor((Date.now() - t) / 60000)
  if (m < 60) return `${Math.max(0, m)}m ago`
  const h = Math.floor(m / 60)
  if (h < 24) return `${h}h ago`
  const d = Math.floor(h / 24)
  if (d < 14) return `${d}d ago`
  return new Date(iso).toLocaleDateString()
}

function flowStatusLabel(summary: NonNullable<Extract<HomeSelection, { kind: 'flow' }>['summary']>): string {
  if (summary.paused) return 'Paused'
  if (!summary.enabled) return 'Disabled'
  return 'Enabled'
}

function runStatusTone(status: string | null | undefined): string {
  if (status === 'completed') return 'text-green-600'
  if (status === 'failed') return 'text-destructive'
  if (status === 'running') return 'text-blue-600'
  return 'text-muted-foreground'
}

function FlowPreviewDetails({ selection }: { selection: Extract<HomeSelection, { kind: 'flow' }> }) {
  const summary = selection.summary
  if (!summary) {
    return (
      <p className="text-sm text-muted-foreground">
        Double-click the row or press Open to continue.
      </p>
    )
  }

  const triggerLabels = summary.triggers.map((t) => t.id).filter(Boolean)
  const uniqueTriggers = [...new Set(triggerLabels)]

  return (
    <div className="space-y-3 text-sm" data-testid={testIds.home.flowPreviewDetails}>
      <div className="flex flex-wrap items-center gap-2">
        <Badge variant="secondary" className="font-normal">
          {flowStatusLabel(summary)}
        </Badge>
        {summary.paused && summary.pausedReason ? (
          <span className="text-xs text-muted-foreground">{summary.pausedReason}</span>
        ) : null}
        {summary.health && summary.health !== 'ok' ? (
          <Badge variant="destructive" className="font-normal capitalize">
            {summary.health}
          </Badge>
        ) : null}
      </div>
      <div className="space-y-1">
        <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Last run</p>
        <p className="text-foreground">
          {formatRelativeTime(summary.lastRunAt)}
          {summary.lastRunStatus ? (
            <span className={cn('ml-2 capitalize', runStatusTone(summary.lastRunStatus))}>
              {summary.lastRunStatus}
            </span>
          ) : null}
        </p>
      </div>
      {uniqueTriggers.length > 0 ? (
        <div className="space-y-1">
          <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Triggers</p>
          <p className="text-muted-foreground">{uniqueTriggers.join(' · ')}</p>
        </div>
      ) : null}
      {summary.tags.length > 0 ? (
        <div className="flex flex-wrap gap-1.5">
          {summary.tags.map((tag) => (
            <Badge key={tag} variant="outline" className="font-normal text-[10px]">
              {tag}
            </Badge>
          ))}
        </div>
      ) : null}
    </div>
  )
}

function BasecampProjectPreviewDetails({
  selection,
}: {
  selection: Extract<HomeSelection, { kind: 'basecamp-project' }>
}) {
  return (
    <div className="space-y-2 text-sm">
      <p className="text-muted-foreground">
        {selection.project.bookmarked ? 'Bookmarked Basecamp project' : 'Basecamp project'}
      </p>
      <p className="text-xs text-muted-foreground">
        Double-click the row or press Open to open the project in Basecamp.
      </p>
    </div>
  )
}

export type HomeSummaryPreviewProps = {
  selection: HomeSelection
  onOpen: (event?: React.MouseEvent) => void
  onClose: () => void
}

/** Compact PanelRight body for non-document Home selections. */
export function HomeSummaryPreview({ selection, onOpen, onClose }: HomeSummaryPreviewProps) {
  const title = titleFromHomeSelection(selection)
  const meta = metaFromHomeSelection(selection)
  const body =
    selection.kind === 'inbox'
      ? selection.item.body?.trim() || null
      : selection.kind === 'suggestion'
        ? selection.description?.trim() || null
        : selection.kind === 'basecamp-todo'
          ? null
          : null

  return (
    <div
      className="h-full flex flex-col bg-card overflow-hidden"
      data-testid={testIds.home.summaryPreview}
    >
      <PreviewPaneHeader
        row
        icon={iconFor(selection)}
        label={`${kindLabel(selection)} Preview`}
        onOpen={onOpen}
        openTitle="Open"
        onClose={onClose}
      />
      <div className="flex-1 min-h-0 overflow-y-auto px-4 py-4 space-y-3">
        <h3 className="text-sm font-medium text-foreground leading-snug">{title}</h3>
        {meta ? <p className="text-xs text-muted-foreground">{meta}</p> : null}
        {selection.kind === 'flow' ? (
          <FlowPreviewDetails selection={selection} />
        ) : selection.kind === 'basecamp-project' ? (
          <BasecampProjectPreviewDetails selection={selection} />
        ) : body ? (
          <MarkdownProse
            content={body}
            linkMode="inbox"
            density="panel"
            testId={testIds.home.summaryPreviewMarkdown}
          />
        ) : selection.kind === 'basecamp-todo' ? (
          <p className="text-sm text-muted-foreground">
            Loading todo preview...
          </p>
        ) : (
          <p className="text-sm text-muted-foreground">
            Double-click the row or press Open to continue.
          </p>
        )}
        <Button size="sm" className="mt-2" onClick={onOpen}>
          Open
        </Button>
      </div>
    </div>
  )
}
